import { useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cr } from "@/lib/wealth";
import { toast } from "sonner";

const BLANK = {
  name: "", borrower_entity_id: "", lender: "", outstanding: "", interest_rate: "",
  maturity_date: "", secured_asset_id: "", recourse_type: "Recourse", personal_guarantee: false,
  periodic_obligation: "", balance_date: "", notes: "",
};
const cls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-navy outline-none focus:border-plum";

export const LiabilityPanel = ({ liabilities, assets, entities, canEdit, reload }) => {
  const { tenantId } = useAuth();
  const [form, setForm] = useState(BLANK);
  const [adding, setAdding] = useState(false);

  const save = async () => {
    try {
      await api.post(`/tenants/${tenantId}/wealth/liabilities`, {
        ...form,
        outstanding: Number(form.outstanding) || 0,
        interest_rate: form.interest_rate === "" ? null : Number(form.interest_rate),
        periodic_obligation: form.periodic_obligation === "" ? null : Number(form.periodic_obligation),
        borrower_entity_id: form.borrower_entity_id || null,
        secured_asset_id: form.secured_asset_id || null,
      });
      toast.success("Liability saved");
      setForm(BLANK); setAdding(false); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (li) => {
    if (!window.confirm(`Delete "${li.name}"?`)) return;
    try {
      await api.delete(`/tenants/${tenantId}/wealth/liabilities/${li.id}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const assetName = (id) => assets.find((a) => a.id === id)?.name;
  const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="liability-panel">
      <div className="panel overflow-x-auto">
        <table className="w-full min-w-[48rem] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left">
              {["Liability", "Borrower", "Lender", "Outstanding", "Secured against", "Balance date", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {liabilities.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">
                No liabilities recorded.
              </td></tr>
            )}
            {liabilities.map((li) => (
              <tr key={li.id} data-testid={`liability-row-${li.id}`}>
                <td className="px-4 py-3 font-medium text-navy">{li.name}</td>
                <td className="px-4 py-3 text-foreground/80">{entityName(li.borrower_entity_id)}</td>
                <td className="px-4 py-3 text-foreground/80">{li.lender}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-destructive">{cr(li.outstanding)}</td>
                <td className="px-4 py-3 text-foreground/80">
                  {li.secured_asset_id ? assetName(li.secured_asset_id) || "asset" : "Standalone"}
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{(li.balance_date || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  {canEdit && (
                    <button onClick={() => remove(li)} data-testid={`liability-delete-${li.id}`}
                            className="rounded-full p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                      <Trash size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && (adding ? (
        <div className="panel space-y-4 p-6" data-testid="liability-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <input placeholder="Liability name" value={form.name} data-testid="liability-name"
                   onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} />
            <input placeholder="Lender" value={form.lender} data-testid="liability-lender"
                   onChange={(e) => setForm({ ...form, lender: e.target.value })} className={cls} />
            <input type="number" step="any" placeholder="Outstanding (₹)" value={form.outstanding}
                   data-testid="liability-outstanding"
                   onChange={(e) => setForm({ ...form, outstanding: e.target.value })} className={cls} />
            <select value={form.borrower_entity_id} data-testid="liability-borrower"
                    onChange={(e) => setForm({ ...form, borrower_entity_id: e.target.value })} className={cls}>
              <option value="">Borrower entity</option>
              {entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
            </select>
            <select value={form.secured_asset_id} data-testid="liability-secured"
                    onChange={(e) => setForm({ ...form, secured_asset_id: e.target.value })} className={cls}>
              <option value="">Standalone (not secured against an asset)</option>
              {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input type="date" value={form.balance_date} data-testid="liability-balance-date"
                   onChange={(e) => setForm({ ...form, balance_date: e.target.value })} className={cls} />
          </div>
          <div className="flex gap-3">
            <button onClick={save} data-testid="liability-save"
                    className="rounded-full bg-plum px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
              Save liability
            </button>
            <button onClick={() => { setAdding(false); setForm(BLANK); }} data-testid="liability-cancel"
                    className="rounded-full border border-border px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-navy hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="liability-add"
                className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-navy/90">
          <Plus size={14} /> Add liability
        </button>
      ))}
    </div>
  );
};
